import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Collapse,
  List,
  ListItemButton,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";

import { SideDrawer } from "../../../components/SideDrawer";
import RemoteDirectoryBrowser from "../components/RemoteDirectoryBrowser";
import MainPanelContent, {
  CONTENT_MAX_WIDTH,
} from "../components/MainPanelContent";
import { useDatasets } from "../../../hooks/useDatasets";
import { datasetBrowsePath } from "../Datasets/datasetDisplay";
import StorageFileReader from "./StorageFileReader";
import {
  ACCENT,
  ACCENT_TINT,
  CANVAS,
  FONT,
  HAIRLINE,
  HOVER,
  ICON,
  INK,
  INK_MUTED,
  RADIUS,
  SHADOW_SM,
  SURFACE,
} from "../../../theme/tokens";

export default function StorageBrowser() {
  const location = useLocation();
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedSource, setSelectedSource] = useState("home");
  const [initialPath, setInitialPath] = useState(".");
  const [datasetsExpanded, setDatasetsExpanded] = useState(false);
  const {
    datasets,
    loading: datasetsLoading,
    error: datasetsError,
  } = useDatasets();

  const browseableDatasets = datasets
    .map((dataset) => ({ dataset, path: datasetBrowsePath(dataset) }))
    .filter(({ path }) => path);

  // Used by the Datasets page to navigate from a dataset card to the contents
  useEffect(() => {
    const target = location.state?.storageBrowserTarget;
    if (!target) return;

    setSelectedFile(null);
    setSelectedSource(target.source === "datasets" ? "datasets" : "home");
    setInitialPath(target.initialPath || ".");
    setDatasetsExpanded(target.source === "datasets");
  }, [location.state]);

  const handleSelectHome = () => {
    setSelectedFile(null);
    setSelectedSource("home");
    setInitialPath(".");
  };

  const handleSelectDatasetRoot = (path) => {
    setSelectedFile(null);
    setSelectedSource("datasets");
    setInitialPath(path || ".");
  };

  const homeSelected = selectedSource === "home";

  return (
    <MainPanelContent
      title="Home Viewer"
      subtitle={
        "Explore your EFabric storage — your home directory or shared datasets. " +
        "Select a location, then click a file to preview it."
      }
      maxWidth={CONTENT_MAX_WIDTH}
      contentGap={0}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          overflow: { xs: "auto", lg: "hidden" },
          backgroundColor: CANVAS,
        }}
      >
        {/* Body: locations (master) + directory browser (detail) */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 2, lg: 3 },
            flex: 1,
            minHeight: 0,
            pb: { xs: 2, sm: 3, lg: 4 },
          }}
        >
          {/* Locations sidebar */}
          <Box
            sx={{
              width: { xs: "100%", lg: 300 },
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              minHeight: { xs: 200, lg: 0 },
              maxHeight: { xs: 360, lg: "none" },
              backgroundColor: SURFACE,
              border: `1px solid ${HAIRLINE}`,
              borderRadius: `${RADIUS.md}px`,
              boxShadow: SHADOW_SM,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 2,
                py: 1.25,
                borderBottom: `1px solid ${HAIRLINE}`,
                flexShrink: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: INK_MUTED,
                }}
              >
                Locations
              </Typography>
            </Box>

            <Box sx={{ overflowY: "auto", flex: 1, p: 1 }}>
              <List disablePadding>
                {/* Home */}
                <ListItemButton
                  selected={homeSelected}
                  onClick={handleSelectHome}
                  sx={{
                    gap: 1.25,
                    px: 1.25,
                    py: 0.85,
                    mb: 0.25,
                    borderRadius: `${RADIUS.sm}px`,
                    "&:hover": { backgroundColor: HOVER },
                    "&.Mui-selected": {
                      backgroundColor: ACCENT_TINT,
                      "&:hover": { backgroundColor: ACCENT_TINT },
                    },
                  }}
                >
                  <HomeRoundedIcon
                    sx={{
                      fontSize: 19,
                      color: homeSelected ? ACCENT : ICON,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: FONT.md,
                      fontWeight: 600,
                      color: homeSelected ? ACCENT : INK,
                    }}
                  >
                    Home
                  </Typography>
                </ListItemButton>

                {/* Datasets group */}
                <ListItemButton
                  onClick={() => setDatasetsExpanded((prev) => !prev)}
                  sx={{
                    gap: 1.25,
                    px: 1.25,
                    py: 0.85,
                    borderRadius: `${RADIUS.sm}px`,
                    "&:hover": { backgroundColor: HOVER },
                  }}
                >
                  <StorageRoundedIcon sx={{ fontSize: 19, color: ICON }} />
                  <Typography
                    sx={{
                      fontSize: FONT.md,
                      fontWeight: 600,
                      color: INK,
                      flex: 1,
                    }}
                  >
                    Datasets
                  </Typography>
                  {datasetsExpanded ? (
                    <ExpandLessIcon sx={{ fontSize: 18, color: ICON }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 18, color: ICON }} />
                  )}
                </ListItemButton>

                <Collapse in={datasetsExpanded} timeout="auto" unmountOnExit>
                  {datasetsLoading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        py: 1.5,
                      }}
                    >
                      <CircularProgress size={16} sx={{ color: ACCENT }} />
                    </Box>
                  ) : datasetsError ? (
                    <Box sx={{ px: 2, py: 1.25, display: "flex", gap: 1 }}>
                      <ChevronRightIcon
                        sx={{ fontSize: 14, color: INK_MUTED, mt: 0.125 }}
                      />
                      <Typography sx={{ fontSize: FONT.sm, color: INK_MUTED }}>
                        Datasets unavailable.
                      </Typography>
                    </Box>
                  ) : browseableDatasets.length ? (
                    <List disablePadding sx={{ pb: 0.5 }}>
                      {browseableDatasets.map(({ dataset, path }) => {
                        const selected =
                          selectedSource === "datasets" && initialPath === path;
                        return (
                          <ListItemButton
                            key={dataset.id}
                            selected={selected}
                            onClick={() => handleSelectDatasetRoot(path)}
                            sx={{
                              gap: 1,
                              pl: 2,
                              pr: 1.25,
                              py: 0.7,
                              ml: 1.5,
                              borderRadius: `${RADIUS.sm}px`,
                              borderLeft: `1px solid ${HAIRLINE}`,
                              "&:hover": { backgroundColor: HOVER },
                              "&.Mui-selected": {
                                backgroundColor: ACCENT_TINT,
                                borderLeftColor: ACCENT,
                                "&:hover": { backgroundColor: ACCENT_TINT },
                              },
                            }}
                          >
                            <FolderRoundedIcon
                              sx={{
                                fontSize: 16,
                                flexShrink: 0,
                                color: selected ? ACCENT : ICON,
                              }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontSize: FONT.sm,
                                  fontWeight: 600,
                                  color: selected ? ACCENT : INK,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {dataset.title}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: FONT.xs,
                                  color: INK_MUTED,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                datasets/{path}
                              </Typography>
                            </Box>
                          </ListItemButton>
                        );
                      })}
                    </List>
                  ) : (
                    <Box sx={{ px: 2, py: 1.25, display: "flex", gap: 1 }}>
                      <ChevronRightIcon
                        sx={{ fontSize: 14, color: INK_MUTED, mt: 0.125 }}
                      />
                      <Typography sx={{ fontSize: FONT.sm, color: INK_MUTED }}>
                        No datasets found.
                      </Typography>
                    </Box>
                  )}
                </Collapse>
              </List>
            </Box>
          </Box>

          {/* Directory browser */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flex: 1,
              minHeight: { xs: 360, lg: 0 },
              minWidth: 0,
            }}
          >
            <RemoteDirectoryBrowser
              key={`${selectedSource}:${initialPath}`}
              height="100%"
              initialPath={initialPath}
              rootLabel={selectedSource === "home" ? "~" : "datasets"}
              rowDensity="comfortable"
              source={selectedSource}
              onFileOpen={setSelectedFile}
            />
          </Box>
        </Box>
      </Box>

      <SideDrawer
        open={Boolean(selectedFile)}
        onClose={() => setSelectedFile(null)}
        title=""
      >
        {selectedFile && (
          <StorageFileReader
            filePath={selectedFile.relativePath}
            source={selectedFile.source || "home"}
          />
        )}
      </SideDrawer>
    </MainPanelContent>
  );
}
